import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Time-Critical & AOG', path: '/time-critical' },
    { name: 'Air Freight', path: '/air-freight' },
    { name: 'ATC Insights', path: '/insights' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    { name: 'Sea Freight', path: '/sea-freight' },
    { name: 'Onboard Courier & Charter', path: '/onboard-courier' },
    { name: 'Dangerous Goods', path: '/dangerous-goods' },
    { name: 'Customs Clearance', path: '/customs' },
  ];

  return (
    <footer className="bg-[#003057]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white inline-block px-5 py-4 rounded mb-4">
              <img
                src="/atc-logo.png"
                alt="Aero Time Critical logo"
                className="h-14 w-auto object-contain"
                style={{ maxWidth: '220px' }}
              />
            </div>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Global freight forwarding excellence with 24/7 support for time-critical and AOG operations.
            </p>
            <div className="flex items-center gap-2 text-[#C9A227] font-semibold">
              <Clock size={18} />
              <span>24/7/365 Available</span>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="text-lg font-semibold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-[#00A3E0] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h4 className="text-lg font-semibold mb-4 text-white">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    className="text-gray-300 hover:text-[#00A3E0] transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h4 className="text-lg font-semibold mb-4 text-white">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+441753720055"
                  className="flex items-start gap-3 text-gray-300 hover:text-[#00A3E0] transition-colors"
                >
                  <Phone size={18} className="mt-1 flex-shrink-0" />
                  <span>+44 (0) 1753 720055</span>
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/447445137566"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-gray-300 hover:text-[#00A3E0] transition-colors"
                >
                  <MessageCircle size={18} className="mt-1 flex-shrink-0" />
                  <span>WhatsApp: +44 7445 137566</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:booking@aerotimecritical.co.uk"
                  className="flex items-start gap-3 text-gray-300 hover:text-[#00A3E0] transition-colors"
                >
                  <Mail size={18} className="mt-1 flex-shrink-0" />
                  <span>booking@aerotimecritical.co.uk</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-300">
                <MapPin size={18} className="mt-1 flex-shrink-0" />
                <span>268 Bath Road, Slough, Berkshire, SL1 4DX</span>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Company legal details */}
        <div className="mt-12 pt-8 border-t border-white/15 text-gray-400 text-sm space-y-1">
          <p><span className="text-gray-300 font-semibold">Aero Time Critical Ltd</span></p>
          <p>Registered office: 268 Bath Road, Slough, Berkshire, SL1 4DX, United Kingdom</p>
          <p>Contact: <a href="mailto:booking@aerotimecritical.co.uk" className="hover:text-[#00A3E0]">booking@aerotimecritical.co.uk</a></p>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-white/15">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Aero Time Critical Ltd. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
              <Link to="/privacy-policy" className="text-sm text-gray-300 hover:text-[#00A3E0] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="text-sm text-gray-300 hover:text-[#00A3E0] transition-colors">
                Cookie Policy
              </Link>
              <Link to="/terms" className="text-sm text-gray-300 hover:text-[#00A3E0] transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/quote" className="text-sm text-gray-300 hover:text-[#00A3E0] transition-colors">
                Request Quote
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
